import { sum, min } from 'lib/array/arrayUtils'
import { mapExperienceCartItemToSummaryViews } from 'checkout/lib/utils/experiences/view'
import { generateExperienceCheckoutTicket } from 'lib/checkout/experiences/cart'
import { getExperienceTimesKey } from 'lib/experiences/experienceUtils'
import type { TripChatItemCartStatus } from 'tripPlanner/api/types'

type CartPanelMappedItineraryCartStatus = Exclude<TripChatItemCartStatus, 'needs_flight_selection'>

export type CartPanelItemCartStatus =
  | CartPanelMappedItineraryCartStatus
  | 'pending_hydration'
  | 'tickets_unavailable'

const KNOWN_ITINERARY_CART_STATUSES = [
  'active',
  'needs_dates',
  'needs_time',
  'duration_unavailable',
  'needs_tickets',
  'needs_flight_selection',
  'not_synced',
] satisfies ReadonlyArray<TripChatItemCartStatus>

const KNOWN_ITINERARY_CART_STATUS_SET: ReadonlySet<string> = new Set(KNOWN_ITINERARY_CART_STATUSES)

function isItineraryCartStatus(cartStatus: string): cartStatus is TripChatItemCartStatus {
  return KNOWN_ITINERARY_CART_STATUS_SET.has(cartStatus)
}

function isCartPanelMappedItineraryCartStatus(
  cartStatus: TripChatItemCartStatus,
): cartStatus is CartPanelMappedItineraryCartStatus {
  return cartStatus !== 'needs_flight_selection'
}

export function resolveCartPanelItemCartStatus(
  cartStatus: string | undefined,
): CartPanelItemCartStatus | undefined {
  if (!cartStatus) return undefined
  if (!isItineraryCartStatus(cartStatus)) return undefined
  if (!isCartPanelMappedItineraryCartStatus(cartStatus)) return undefined
  return cartStatus
}

interface CartPanelTotalItem {
  cartExcluded: boolean
  price: number | undefined
  memberPrice: number | undefined
}

export function computeCartPanelTotal(
  items: Array<CartPanelTotalItem>,
  hasLuxPlusBenefits: boolean,
): { totalPrice: number; totalItemCount: number } {
  const includedItems = items.filter(item => !item.cartExcluded)
  return {
    totalPrice: sum(includedItems, item => {
      const member = hasLuxPlusBenefits ? item.memberPrice : undefined
      return member || (item.price ?? 0)
    }),
    totalItemCount: includedItems.length,
  }
}

export interface CartPanelCheckoutReadinessItem {
  source: 'cart' | 'itinerary'
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  pricing: {
    isAvailable: boolean | undefined
  }
  cartItem: App.MultiCartItem | undefined
}

export function isCartPanelItemCheckoutReady(item: CartPanelCheckoutReadinessItem): boolean {
  return (
    item.source === 'cart' &&
    item.cartStatus === 'active' &&
    !item.cartExcluded &&
    item.pricing.isAvailable !== false &&
    !!item.cartItem
  )
}

export function shouldShowReadyItemsOnlyNote(items: Array<CartPanelCheckoutReadinessItem>): boolean {
  const includedItems = items.filter(item => !item.cartExcluded)
  return (
    includedItems.some(isCartPanelItemCheckoutReady) &&
    includedItems.some(item => !isCartPanelItemCheckoutReady(item))
  )
}

export function isExperienceBuyNowBookLaterBookingType(bookingType: string | undefined): boolean {
  return bookingType === 'NO-CALENDAR-FIXED-END' || bookingType === 'NO-CALENDAR-FIXED-VALIDITY'
}

export function isCartPanelExperienceTimeRequired(
  bookingType: string | undefined,
  isBuyNowBookLater: boolean,
  isGift: boolean,
): boolean {
  return bookingType === 'CALENDAR-TIMESLOTS' && !isBuyNowBookLater && !isGift
}

export function getCartPanelExperienceCartTime(
  cartItem: App.MultiCartItem | undefined,
): string | undefined {
  if (cartItem?.type !== 'experience') return undefined
  return cartItem.time
}

export interface CartPanelExperienceStatusInput {
  cartStatus: CartPanelItemCartStatus
  bookingType: string | undefined
  isBuyNowBookLater: boolean
  isGift: boolean
  cartItem: App.MultiCartItem | undefined
}

export function resolveCartPanelExperienceCartStatus({
  cartStatus,
  bookingType,
  isBuyNowBookLater,
  isGift,
  cartItem,
}: CartPanelExperienceStatusInput): CartPanelItemCartStatus {
  if (cartStatus !== 'active') return cartStatus
  if (!isCartPanelExperienceTimeRequired(bookingType, isBuyNowBookLater, isGift)) return cartStatus
  if (!cartItem) return cartStatus

  return getCartPanelExperienceCartTime(cartItem) ? cartStatus : 'needs_time'
}

export interface CartPanelExperienceDisplayStatusInput extends CartPanelExperienceStatusInput {
  hasSelectedTickets: boolean
  isAvailable: boolean | undefined
}

export function resolveCartPanelExperienceDisplayStatus({
  hasSelectedTickets,
  isAvailable,
  ...statusInput
}: CartPanelExperienceDisplayStatusInput): CartPanelItemCartStatus {
  const cartStatus = resolveCartPanelExperienceCartStatus(statusInput)
  if (cartStatus !== 'active') return cartStatus
  if (isAvailable === false && hasSelectedTickets) return 'tickets_unavailable'
  return cartStatus
}

interface ResolvedPrice {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
}

export function resolveItemPrice(
  offer: App.HotelOffer | undefined,
  packageId: string | undefined,
): ResolvedPrice {
  if (!offer || offer.packages.length === 0) {
    return { price: undefined, memberPrice: undefined, value: undefined }
  }

  if (packageId) {
    const matched = offer.packages.find(pkg => pkg.id === packageId)
    if (matched) {
      return {
        price: matched.price,
        memberPrice: matched.memberPrice,
        value: matched.value,
      }
    }
  }

  const cheapest = min(offer.packages, pkg => pkg.price)

  return {
    price: cheapest?.price,
    memberPrice: cheapest?.memberPrice,
    value: cheapest?.value,
  }
}

export interface CartPanelExperienceTicket {
  ticketId: string
  name: string
  count: number
  date?: string
}

export interface ExperiencePricingInput {
  experience: App.ExperienceOffer | undefined
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot> | undefined
  tickets: Array<CartPanelExperienceTicket>
  date: string | undefined
  time: string | undefined
  isBuyNowBookLater: boolean
  savedPrice: number | undefined
  savedMemberPrice: number | undefined
  savedValue: number | undefined
  loading: boolean
  error: boolean
}

export interface CartPanelExperienceTimesKeyInput {
  experienceId: string
  date?: string
  pickupPointId?: string
  redemptionLocationId?: string
  isBuyNowBookLater: boolean
}

export function getCartPanelExperienceTimesKey(
  item: CartPanelExperienceTimesKeyInput,
  currency: string,
): string {
  return getExperienceTimesKey(item.experienceId, item.date, {
    currency,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
    isBuyNowBookLater: item.isBuyNowBookLater,
  })
}

export interface ExperienceResolvedPricing {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
  isAvailable: boolean | undefined
  loading: boolean
  error: boolean
  hasUnavailableTickets: boolean
}

function toCheckoutExperienceTicket(
  ticket: CartPanelExperienceTicket,
  liveTicket: App.ExperienceItemTicket | undefined,
): App.Checkout.ExperienceItemTicket {
  return {
    ticketId: ticket.ticketId,
    name: ticket.name,
    fareType: liveTicket?.fareType,
    count: ticket.count,
    price: liveTicket?.price ?? 0,
    memberPrice: liveTicket?.memberPrice ?? 0,
    discount: liveTicket?.discount ?? 0,
    date: ticket.date,
  }
}

function findExperienceTicket(
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot>,
  ticket: CartPanelExperienceTicket,
  requiresTime: boolean,
  selectedTime: string | undefined,
): App.ExperienceItemTicket | undefined {
  for (const slot of timeSlots) {
    if (requiresTime && slot.time !== selectedTime) continue
    const matched = slot.tickets.find(liveTicket => (
      liveTicket.productId === ticket.ticketId &&
      liveTicket.date === ticket.date
    ))
    if (matched) return matched
  }
}

export function prepareCartPanelExperienceCheckoutItem(
  cartItem: App.Checkout.ExperienceItem,
  experience: App.ExperienceOffer | undefined,
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot> | undefined,
): App.Checkout.ExperienceItem {
  if (!experience || !timeSlots) return cartItem

  const requiresTime = experience.bookingType === 'CALENDAR-TIMESLOTS' && !cartItem.isBuyNowBookLater && !cartItem.isGift
  const tickets = cartItem.tickets.map(ticket => {
    const liveTicket = findExperienceTicket(timeSlots, ticket, requiresTime, cartItem.time)
    if (!liveTicket) return ticket

    // TripPlanner stores svc-trip ticketId as the upstream productId. Checkout
    // selectors currently key cart tickets by availability id, so translate at
    // this boundary after matching the canonical productId.
    return {
      ...ticket,
      ...generateExperienceCheckoutTicket({ ticket: liveTicket, count: ticket.count }),
    }
  })

  return {
    ...cartItem,
    tickets,
    total: sum(tickets, ticket => ticket.price * ticket.count),
    memberTotal: sum(tickets, ticket => ticket.memberPrice * ticket.count),
  }
}

export function resolveExperiencePricing({
  experience,
  timeSlots,
  tickets,
  time,
  isBuyNowBookLater,
  savedPrice,
  savedMemberPrice,
  savedValue,
  loading,
  error,
}: ExperiencePricingInput): ExperienceResolvedPricing {
  if (tickets.length > 0 && experience && timeSlots) {
    const requiresTime = experience.bookingType === 'CALENDAR-TIMESLOTS' && !isBuyNowBookLater
    const ticketViews = tickets.map(ticket => {
      const liveTicket = findExperienceTicket(timeSlots, ticket, requiresTime, time)
      return mapExperienceCartItemToSummaryViews(
        toCheckoutExperienceTicket(ticket, liveTicket),
        liveTicket,
      )
    })

    const hasUnavailableTickets = ticketViews.some(ticketView => ticketView?.unavailable)
    const availableTicketViews = ticketViews.filter(ticketView => ticketView && !ticketView.unavailable)

    if (hasUnavailableTickets) {
      return {
        price: undefined,
        memberPrice: undefined,
        value: undefined,
        isAvailable: false,
        loading,
        error,
        hasUnavailableTickets,
      }
    }

    return {
      price: sum(availableTicketViews, ticketView => ticketView?.price ?? 0),
      memberPrice: sum(availableTicketViews, ticketView => ticketView?.memberPrice ?? 0),
      value: sum(availableTicketViews, ticketView => ticketView?.value ?? 0),
      isAvailable: true,
      loading,
      error,
      hasUnavailableTickets,
    }
  }

  if (tickets.length > 0) {
    return {
      price: savedPrice ?? experience?.price ?? experience?.pricing.price,
      memberPrice: savedMemberPrice ?? experience?.pricing.memberPrice,
      value: savedValue ?? experience?.pricing.value,
      isAvailable: experience ? experience.status === 'ONLINE' : undefined,
      loading,
      error,
      hasUnavailableTickets: false,
    }
  }

  return {
    price: experience?.price ?? experience?.pricing.price ?? savedPrice,
    memberPrice: experience?.pricing.memberPrice ?? savedMemberPrice,
    value: experience?.pricing.value ?? savedValue,
    isAvailable: experience ? experience.status === 'ONLINE' : undefined,
    loading,
    error,
    hasUnavailableTickets: false,
  }
}
